import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AnimationRevealPage from "helpers/AnimationRevealPage";
import { HeaderStyles, ContainerStyles, HeadingStyles, TextStyles, FeedbackStyles } from "styles/style";
import { api } from "supabase/database/api.ts";
import { ReactComponent as TrashIcon } from "feather-icons/dist/icons/trash-2.svg";
import tw from "twin.macro";
import styled from "styled-components";

const BookmarkList = tw.div`mt-6 space-y-3`;
const GroupTitle = tw.h2`text-lg font-bold text-gray-700 mt-6 mb-2`;
const TableTag = tw.span`text-xs font-normal text-gray-400 ml-2 normal-case`;
const BookmarkItem = tw.div`flex items-center justify-between p-3 bg-gray-200 rounded-lg hover:bg-gray-100 cursor-pointer border border-gray-200`;
const BookmarkLabel = tw.span`text-sm text-gray-800`;
const PageBadge = tw.span`ml-2 text-xs bg-gray-100 text-primary-700 px-2 py-1 rounded-full`;
const DeleteBtn = styled.button`
  ${tw`p-1 text-gray-400 hover:text-red-500 transition-colors`}
  svg { ${tw`w-4 h-4`} }
`;
const EmptyHint = tw.p`text-gray-400 text-center mt-16`;

export default function BookmarksPage() {
  const navigate = useNavigate();
  const [bookmarks, setBookmarks] = useState(null);

  useEffect(() => {
    api.bookmarkQueries.getBookmarks()
      .then(data => setBookmarks(data || []))
      .catch(() => setBookmarks([]));
  }, []);

  async function handleDelete(e, id) {
    e.stopPropagation();
    await api.bookmarkMutations.removeBookmark({ id });
    setBookmarks(prev => prev.filter(b => b.id !== id));
  }

  function handleClick(b) {
    const path = b.content_path || `/${b.table_name}/${b.content_id}`;
    navigate(b.page_number ? `${path}?page=${b.page_number}` : path);
  }

  const groups = bookmarks
    ? Object.values(
        bookmarks.reduce((acc, b) => {
          const isDoc = b.page_number === null || b.page_number === undefined;
          const key = isDoc ? `doc::${b.table_name}` : `${b.table_name}::${b.content_id}`;
          const title = isDoc
            ? b.table_name.replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase())
            : b.content_title || b.content_id;
          if (!acc[key]) acc[key] = { title, table_name: b.table_name, content_id: b.content_id, items: [], isDoc };
          acc[key].items.push(b);
          return acc;
        }, {})
      )
    : [];

  return (
    <AnimationRevealPage>
      <HeaderStyles.ContentStyledHeader />
      <ContainerStyles.Container>
        <ContainerStyles.ContentWithPaddingXl>
          <HeadingStyles.HeadingRow>
            <HeadingStyles.Heading>
              <TextStyles.HighlightedText>Bookmarks</TextStyles.HighlightedText>
            </HeadingStyles.Heading>
          </HeadingStyles.HeadingRow>

          {!bookmarks && (
            <>
              <FeedbackStyles.SkeletonHeading />
              <FeedbackStyles.SkeletonLine />
              <FeedbackStyles.SkeletonShortLine />
            </>
          )}

          {bookmarks && bookmarks.length === 0 && (
            <EmptyHint>No bookmarks yet. Open a PDF and bookmark a page to get started.</EmptyHint>
          )}

          {groups.map(group => (
            <div key={`${group.table_name}::${group.content_id}`}>
              <GroupTitle
                onClick={() => navigate(group.items[0]?.content_path || `/${group.table_name}/${group.content_id}`)}
                tw="cursor-pointer hover:text-primary-500"
              >
                {group.title}
              </GroupTitle>
              <BookmarkList>
                {group.items.map(b => (
                  <BookmarkItem key={b.id} onClick={() => handleClick(b)}>
                    <div>
                      <BookmarkLabel>
                        {b.label || (b.page_number ? `Page ${b.page_number}` : b.content_title || "Document")}
                      </BookmarkLabel>
                      {b.page_number && <PageBadge>p.{b.page_number}</PageBadge>}
                    </div>
                    <DeleteBtn onClick={(e) => handleDelete(e, b.id)} title="Remove bookmark">
                      <TrashIcon />
                    </DeleteBtn>
                  </BookmarkItem>
                ))}
              </BookmarkList>
            </div>
          ))}
        </ContainerStyles.ContentWithPaddingXl>
      </ContainerStyles.Container>
    </AnimationRevealPage>
  );
}
